import {
  Badge,
  Box,
  HStack,
  Text,
  VStack
} from "@chakra-ui/react"
import { CardDivider, CardMetric } from "../cards/CardParts"
import { StreetViewImage } from "../common/StreetViewImage"
import { formatCurrency, formatPayback, formatRoi, getRoiColor } from "../../utils/formatters"
import { Home } from "../../services/portfolioApi"

export const getEpcBadge = (rating: string | null | undefined) => {
  if (!rating) return null
  const r = rating.toUpperCase()
  const colorPalette =
    r === "A" || r === "B" ? "green"
    : r === "C" ? "teal"
    : r === "D" ? "yellow"
    : r === "E" ? "orange"
    : "red"
  return { colorPalette, label: `EPC ${r}` }
}

export const getStatusBadge = (status: Home["status"]) => {
  if (status === "missing_data") return { colorPalette: "orange" as const, label: "Missing Data" }
  if (status !== "analysed") return { colorPalette: "red" as const, label: "Analysis Failed" }
  return null
}

export interface PropertyCardProps {
  home: Home
  onClick: (id: number) => void
  showTenantPowerRoi?: boolean
}

export const getHomePropertyDetailLine = (home: Home) =>
  [
    home.features.propertyType || null,
    home.features.builtForm || null,
    home.features.floorArea ? `${home.features.floorArea} m²` : null,
    home.features.storeys ? `${home.features.storeys} storeys` : null,
  ]
    .filter(Boolean)
    .join(" · ")

const PropertyCard = ({ home, onClick, showTenantPowerRoi = false }: PropertyCardProps) => {
  const epcBadge = getEpcBadge(home.features.currentEpcRating)
  const statusBadge = getStatusBadge(home.status)
  const activeRoi = showTenantPowerRoi ? home.tenantPowerRoi : home.upgradesRoi
  const activePayback = showTenantPowerRoi ? home.tenantPowerPaybackPeriod : home.upgradesPaybackPeriod
  const roiColor = showTenantPowerRoi ? "#5840FF" : getRoiColor(activeRoi)
  const roiLabel = showTenantPowerRoi ? "Tenant Power ROI" : "Green ROI"
  const isAnalysed = home.status === "analysed"

  const propertyDetailLine = getHomePropertyDetailLine(home)

  return (
    <Box
      bg="white"
      borderRadius="xl"
      overflow="hidden"
      boxShadow="sm"
      border="1px solid"
      borderColor="gray.100"
      cursor="pointer"
      transition="all 0.15s"
      _hover={{ boxShadow: "md", borderColor: "green.200", transform: "translateY(-1px)" }}
      onClick={() => onClick(home.id)}
    >
      {/* Desktop */}
      <HStack gap={0} align="stretch" display={{ base: "none", md: "flex" }}>
        <Box w="160px" minW="160px" flexShrink={0} bg="gray.100" overflow="hidden">
          <StreetViewImage
            imageUrl={home.imageUrl}
            address={home.fullAddress}
            svSize="400x300"
            alt={home.fullAddress}
            w="100%"
            h="100%"
            minH="90px"
            objectFit="cover"
          />
        </Box>

        <VStack align="start" justify="center" gap={1} flex={1} px={5} py={4}>
          <Text fontWeight="semibold" fontSize="md" color="gray.900" lineClamp={1}>
            {home.fullAddress}
          </Text>
          {propertyDetailLine && (
            <Text fontSize="sm" color="gray.500">{propertyDetailLine}</Text>
          )}
          <HStack gap={2} mt={1} flexWrap="wrap">
            {statusBadge && (
              <Badge colorPalette={statusBadge.colorPalette} variant="subtle" fontSize="xs">
                {statusBadge.label}
              </Badge>
            )}
            {epcBadge && (
              <Badge colorPalette={epcBadge.colorPalette} variant="subtle" fontSize="xs">
                {epcBadge.label}
              </Badge>
            )}
          </HStack>
        </VStack>

        {isAnalysed && (
          <HStack gap={0} align="center" pr={5} flexShrink={0}>
            <CardDivider />
            <Box px={5}>
              <CardMetric
                label={roiLabel}
                value={
                  <Text fontSize="lg" fontWeight="semibold" color={roiColor}>
                    {formatRoi(activeRoi)}
                  </Text>
                }
              />
            </Box>
            <CardDivider />
            <Box px={5}>
              <CardMetric
                label="Upgrade Cost"
                value={
                  <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                    {formatCurrency(home.upgradesTotalCost)}
                  </Text>
                }
              />
            </Box>
            <CardDivider />
            <Box px={5}>
              <CardMetric
                label="Payback"
                value={
                  <Text fontSize="lg" fontWeight="semibold" color={showTenantPowerRoi ? "#5840FF" : "gray.800"}>
                    {formatPayback(activePayback)}
                  </Text>
                }
              />
            </Box>
          </HStack>
        )}
      </HStack>

      {/* Mobile */}
      <VStack align="stretch" gap={0} display={{ base: "flex", md: "none" }}>
        <StreetViewImage
          imageUrl={home.imageUrl}
          address={home.fullAddress}
          svSize="400x300"
          alt={home.fullAddress}
          w="100%"
          h="180px"
          objectFit="cover"
        />
        <VStack align="stretch" gap={3} p={4}>
          <VStack align="start" gap={0.5}>
            <Text fontWeight="semibold" fontSize="md" color="gray.900">
              {home.fullAddress}
            </Text>
            {propertyDetailLine && (
              <Text fontSize="sm" color="gray.500">{propertyDetailLine}</Text>
            )}
            <HStack gap={2} mt={1} flexWrap="wrap">
              {statusBadge && (
                <Badge colorPalette={statusBadge.colorPalette} variant="subtle" fontSize="xs">
                  {statusBadge.label}
                </Badge>
              )}
              {epcBadge && (
                <Badge colorPalette={epcBadge.colorPalette} variant="subtle" fontSize="xs">
                  {epcBadge.label}
                </Badge>
              )}
            </HStack>
          </VStack>
          {isAnalysed && (
            <HStack gap={6} flexWrap="wrap">
              <CardMetric
                label={roiLabel}
                value={
                  <Text fontSize="md" fontWeight="semibold" color={roiColor}>
                    {formatRoi(activeRoi)}
                  </Text>
                }
              />
              <CardMetric
                label="Upgrade Cost"
                value={
                  <Text fontSize="md" fontWeight="semibold" color="gray.800">
                    {formatCurrency(home.upgradesTotalCost)}
                  </Text>
                }
              />
              <CardMetric
                label="Payback"
                value={
                  <Text fontSize="md" fontWeight="semibold" color="gray.800">
                    {formatPayback(activePayback)}
                  </Text>
                }
              />
            </HStack>
          )}
        </VStack>
      </VStack>
    </Box>
  )
}

export default PropertyCard
